import { useRouter } from 'next/router';
import classNames from 'classnames';
import { appendQueryParams, createAppRoutes, useApiErrorHandler, useHubSwitch, showToast } from '@bpm-web-app/utils';
import { useForm } from 'react-hook-form';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useContests } from '@bpm-web-app/swr-hooks';
import { apiKey } from '@bpm-web-app/api-client';
import styles from './submit-remix.module.css';
import UploadIcon from '../../assets/icons/create/upload.svg';
import Title from '../title/title';
import { Checkbox } from '../shared/ui';
import { ContestTermsPreview } from '../shared/contest-terms-preview/contest-terms-preview';
import { AppLink } from '../shared/app-link/app-link';

const TERMS_REQUIRED_MESSAGE = 'You must agree to the terms and conditions before submitting your remix.';

export interface SubmitRemixProps {
    contestDetailPath?: (slug: string) => string;
}

export function SubmitRemix({ contestDetailPath = createAppRoutes.contestDetail }: SubmitRemixProps) {
    const router = useRouter();
    const { slug } = router.query;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alreadySubmittedSuccessfully, setAlreadySubmittedSuccessfully] = useState(false);
    const [file, setFile] = useState(null);
    const errorHandler = useApiErrorHandler();
    const { hub } = useHubSwitch();

    const fileInputRef = useRef(null);

    const { contests, isLoading, error } = useContests();

    const detailedContest = useMemo(() => {
        const contest = contests?.current?.find((contestObj) => contestObj.slug === slug);
        if (contest) {
            return contest;
        }
        return contests?.past?.find((contestObj) => contestObj.slug === slug);
    }, [contests, slug]);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: useMemo(
            () => ({
                title: '',
                description: '',
                acceptedTerms: false,
            }),
            []
        ),
    });

    const acceptedTerms = watch('acceptedTerms');

    const onSubmit = async (formData) => {
        if (!file) {
            showToast({ type: 'error', message: 'File upload required.' });
            return;
        }

        setIsSubmitting(true);

        try {
            const finalFormData = new FormData();
            finalFormData.append('file', file, file.name);
            finalFormData.append('title', formData.title);
            finalFormData.append('description', formData.description);
            const key = apiKey();
            const headers = {} as { [key: string]: string };
            if (key) {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                headers['Authorization'] = key;
            }
            // eslint-disable-next-line @typescript-eslint/dot-notation
            const response = await fetch(`${process.env['NEXT_PUBLIC_CREATE_API_BASE_PATH']}/contest/${detailedContest?.id}/submit`, {
                method: 'POST',
                credentials: 'include',
                body: finalFormData,
                headers,
            });

            if (!response.ok) {
                // NOTE(paulomartinsbynd): this means user already submitted remix before
                // https://github.com/bpmsupreme/documentation/issues/109
                if (response.status === 403) {
                    showToast({ type: 'error', message: 'Remix submission limit reached.' });
                    setAlreadySubmittedSuccessfully(true);
                    return;
                }
                throw new Error(response.statusText);
            }

            setAlreadySubmittedSuccessfully(true);
            showToast({ type: 'success', message: 'File upload successful.' });
        } catch (e) {
            errorHandler({ error: e });
        }

        setIsSubmitting(false);
    };

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    /* TODO: implement (ghost?) loading */
    if (isLoading) return null;

    if (!isLoading && (error || !detailedContest)) return null;

    return (
        <>
            <Title platform={hub} title={detailedContest?.title} />
            <div className={styles['submit-remix']}>
                {detailedContest.banner_image_url && (
                    <div className={styles['submit-remix__img-wrapper']}>
                        <picture className={styles['submit-remix__img']}>
                            <source srcSet={detailedContest.banner_image_url} media="(min-width: 1920px)" />
                            <source
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 3840 })} 2x, ${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1920 })} 1x`}
                                media="(min-width: 1440px)"
                            />
                            <source
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 2880 })} 2x, ${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1440 })} 1x`}
                                media="(min-width: 1024px)"
                            />
                            <source
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 2048 })} 2x, ${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1024 })} 1x`}
                                media="(min-width: 768px)"
                            />
                            <img
                                src={appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 1024 })}
                                srcSet={`${appendQueryParams(detailedContest.banner_image_url, { key: 'dw', value: 2048 })} 2x`}
                                alt={detailedContest.title || ''}
                            />
                        </picture>
                    </div>
                )}
                <div className={styles['submit-remix__intro']}>
                    <div className={styles['submit-remix__summary']}>
                        <h1 className={styles['submit-remix__title']}>Submit for a chance to win</h1>
                        <ul className={styles['submit-remix__details']}>
                            <li className={styles['submit-remix__detail']}>Only one entry per person per contest is permitted</li>
                            {alreadySubmittedSuccessfully && (
                                <li className={styles['submit-remix__detail']}>
                                    Your submission was submitted successfully! Winner will be chosen on {dayjs(detailedContest.announcement).format('MMMM D, YYYY, hh:mma')}.
                                </li>
                            )}
                        </ul>
                        {alreadySubmittedSuccessfully && slug && (
                            <div className={styles['submit-remix__summary-ctas']}>
                                <AppLink href={contestDetailPath(`${slug}`)}>
                                    <a className={classNames(styles['submit-remix__summary-cta'], styles['submit-remix__summary-cta--secondary'])}>
                                        Go Back to Contest
                                    </a>
                                </AppLink>
                            </div>
                        )}
                        {!alreadySubmittedSuccessfully && (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className={styles['submit-remix__form-container']}>
                                    <input {...register('title', { required: true })} type="text" placeholder="Title of Your Track" className={styles['submit-remix__input']} />
                                    <span>{errors?.title?.message}</span>
                                    <input {...register('description', { required: true })} type="text" placeholder="Artist Name" className={styles['submit-remix__input']} />
                                    <span>{errors?.description?.message}</span>
                                    <input ref={fileInputRef} type="file" accept=".wav" onChange={handleFileUpload} multiple={false} hidden />
                                    <button type="button" onClick={() => fileInputRef.current.click()} className={styles['submit-remix__upload-row']}>
                                        <div className={styles['submit-remix__upload-row__title']}>{file === null ? <span>Upload Your Track as a .wav File Only (30MB Limit)</span> : <span>{file.name}</span>}</div>
                                        <div className={styles['submit-remix__upload-row__action']}>
                                            <UploadIcon />
                                            <span>Upload Track</span>
                                        </div>
                                    </button>
                                </div>
                                <div className={styles['submit-remix__terms']}>
                                    <p className={styles['submit-remix__terms-text']}>
                                        Before submitting your remix, please agree to the contest terms and conditions.
                                    </p>
                                    {detailedContest.terms_pdf_url && (
                                        <ContestTermsPreview pdfUrl={detailedContest.terms_pdf_url} linkClassName={styles['submit-remix__terms-link']} />
                                    )}
                                    <div className={styles['submit-remix__terms-checkbox']}>
                                        <Checkbox
                                            {...register('acceptedTerms', {
                                                validate: (value) => Boolean(value) || TERMS_REQUIRED_MESSAGE,
                                            })}
                                            checked={Boolean(acceptedTerms)}
                                            onChange={(e) => setValue('acceptedTerms', e.target.checked, { shouldValidate: true })}
                                        />
                                        <button
                                            type="button"
                                            className={styles['submit-remix__terms-checkbox-text']}
                                            onClick={() => setValue('acceptedTerms', !acceptedTerms, { shouldValidate: true })}
                                        >
                                            I agree to the contest terms and conditions.
                                        </button>
                                    </div>
                                    {errors.acceptedTerms && <p className={styles['submit-remix__terms-error']}>{errors.acceptedTerms.message}</p>}
                                </div>
                                <div className={styles['submit-remix__summary-ctas']}>
                                    <button disabled={isSubmitting} type="submit" className={classNames(styles['submit-remix__summary-cta'], styles['submit-remix__summary-cta--primary'])}>
                                        Submit
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
